import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import useAxiosAuth from "../hooks/useAxiosAuth";

// The Users module: a tenant's own employees (`/staff`) and the sub-roles they
// hold (`/user-roles`). One slice because they are one screen's worth of work —
// you cannot create a user without picking a role, and the role list is only
// ever read in order to assign one.
//
// NOTE ON NAMING. The UI says "Users"; the API says `staff`. The auth table is
// already called `users` and the Sequelize model is already `User`, so the
// backend needed a different word for the profile. Only the wire format uses it.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

// ---------------------------------------------------------------- staff ----

export const getStaffList = createAsyncThunk(
    'staff/getStaffList',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', userRoleId = [], department = [], page = 1, limit = 10 } = params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            if (userRoleId?.length) qs.append('userRoleId', userRoleId.join(','));
            if (department?.length) qs.append('department', department.join(','));
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/staff?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const getStaffById = createAsyncThunk(
    'staff/getStaffById',
    async (staffId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/staff/${staffId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const createStaff = createAsyncThunk(
    'staff/createStaff',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/staff`, payload);
            return response.data;
        } catch (error) {
            // Keep the raw body too: the login-policy 400s carry a machine
            // code + field that AddUser maps to a translated message.
            return rejectWithValue({ error: getSerializableError(error), data: error.response?.data || null });
        }
    }
);

export const updateStaff = createAsyncThunk(
    'staff/updateStaff',
    async ({ staffId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/staff/${staffId}`, data);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error), data: error.response?.data || null });
        }
    }
);

export const setStaffStatus = createAsyncThunk(
    'staff/setStaffStatus',
    async ({ staffId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/staff/${staffId}/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const deleteStaff = createAsyncThunk(
    'staff/deleteStaff',
    async (staffId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/staff/${staffId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Admin-initiated reset. No password goes either way (owner, 2 Oct 2026): the
// server locks the current one and tells the employee to use Forgot password;
// the answer says whether that message went out (data.noticeDelivered).
export const resetStaffPassword = createAsyncThunk(
    'staff/resetStaffPassword',
    async ({ staffId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/staff/${staffId}/password`, {});
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

/** The signed-in employee's own profile. */
export const getMyStaffProfile = createAsyncThunk(
    'staff/getMyStaffProfile',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/staff/me`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// A staff login's OWN profile (PUT /staff/me): name, surname, lang, and the
// sign-in contacts — each contact only WITH its proof ({ email, emailProof } /
// { phone, phoneProof }), the same contract as PUT /admins/:id. The profile is
// re-read afterwards so the verified flags shown are the server's, never ours.
export const updateMyStaffProfile = createAsyncThunk(
    'staff/updateMyStaffProfile',
    async (data, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            await axiosInstance.put(`/staff/me`, data);
            const response = await axiosInstance.get(`/staff/me`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error), data: error.response?.data || null });
        }
    }
);

// ----------------------------------------------------------- user roles ----

export const getUserRoles = createAsyncThunk(
    'staff/getUserRoles',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', page = 1, limit = 50 } = params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/user-roles?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const createUserRole = createAsyncThunk(
    'staff/createUserRole',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/user-roles`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const updateUserRole = createAsyncThunk(
    'staff/updateUserRole',
    async ({ userRoleId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/user-roles/${userRoleId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const setUserRoleStatus = createAsyncThunk(
    'staff/setUserRoleStatus',
    async ({ userRoleId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/user-roles/${userRoleId}/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const deleteUserRole = createAsyncThunk(
    'staff/deleteUserRole',
    async (userRoleId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/user-roles/${userRoleId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

/**
 * The permission catalogue — what a role CAN be granted.
 *
 * Fetched, never bundled. The backend owns config/staffPermissions.js and trims
 * the list to the features this hospital has; keeping a copy here is how
 * data/accessControls.js drifted from its backend twin, and this list is far
 * longer.
 */
export const getPermissionCatalogue = createAsyncThunk(
    'staff/getPermissionCatalogue',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/user-roles/catalogue`);
            return response.data?.groups || [];
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const staffSlice = createSlice({
    name: "staff",
    initialState: {
        staffList: [],
        staffTotal: 0,
        staffTotalPages: 1,
        staffListLoading: false,
        selectedStaff: null,
        myProfile: null,

        roles: [],
        rolesLoading: false,

        catalogue: [],
        catalogueLoading: false,

        mutating: false,
        error: null,
    },
    reducers: {
        resetStaffState: (state) => {
            state.staffList = [];
            state.staffTotal = 0;
            state.staffTotalPages = 1;
            state.selectedStaff = null;
            state.error = null;
        },
        clearStaffError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getStaffList.pending, (state) => { state.staffListLoading = true; state.error = null; })
            .addCase(getStaffList.fulfilled, (state, action) => {
                state.staffListLoading = false;
                state.staffList = action.payload?.staff || [];
                state.staffTotal = action.payload?.total || 0;
                state.staffTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getStaffList.rejected, (state, action) => {
                state.staffListLoading = false;
                state.error = action.payload?.error || 'Failed to load users';
            })

            .addCase(getStaffById.fulfilled, (state, action) => { state.selectedStaff = action.payload; })
            .addCase(getMyStaffProfile.fulfilled, (state, action) => { state.myProfile = action.payload; })
            .addCase(updateMyStaffProfile.fulfilled, (state, action) => { state.myProfile = action.payload; })

            .addCase(getUserRoles.pending, (state) => { state.rolesLoading = true; })
            .addCase(getUserRoles.fulfilled, (state, action) => {
                state.rolesLoading = false;
                state.roles = action.payload?.roles || [];
            })
            .addCase(getUserRoles.rejected, (state, action) => {
                state.rolesLoading = false;
                state.error = action.payload?.error || 'Failed to load user roles';
            })

            .addCase(getPermissionCatalogue.pending, (state) => { state.catalogueLoading = true; })
            .addCase(getPermissionCatalogue.fulfilled, (state, action) => {
                state.catalogueLoading = false;
                state.catalogue = action.payload || [];
            })
            .addCase(getPermissionCatalogue.rejected, (state) => { state.catalogueLoading = false; })

            // Every mutation shares one flag; the pages refetch on success.
            .addMatcher(
                (a) => a.type.startsWith('staff/') && a.type.endsWith('/pending') &&
                    /create|update|delete|setStaffStatus|setUserRoleStatus|resetStaffPassword/.test(a.type),
                (state) => { state.mutating = true; state.error = null; }
            )
            .addMatcher(
                (a) => a.type.startsWith('staff/') && a.type.endsWith('/fulfilled') &&
                    /create|update|delete|setStaffStatus|setUserRoleStatus|resetStaffPassword/.test(a.type),
                (state) => { state.mutating = false; }
            )
            .addMatcher(
                (a) => a.type.startsWith('staff/') && a.type.endsWith('/rejected') &&
                    /create|update|delete|setStaffStatus|setUserRoleStatus|resetStaffPassword/.test(a.type),
                (state, action) => {
                    state.mutating = false;
                    state.error = action.payload?.error || 'Action failed';
                }
            );
    },
});

export const { resetStaffState, clearStaffError } = staffSlice.actions;
export default staffSlice.reducer;
